import React, { Component } from 'react';

import { Card, CardSubtitle, ErrorMessage, Loading, VersionInfo, Wrapper } from './styled/SettingsStyled';

import RadioButtons from '../common/RadioButtons/RadioButtons';

import useLocalization from '../../localization/useLocalization';
import { locale as localeUtils } from '../../utils/utils';
import packageJSON from '../../../package.json';
import NickName from './NickName/SettingsNickName';
import TelephonyID from './TelephonyID/TelephonyID';
import { getDebugInfo } from '../../utils/debug';
import { IS_ELECTRON_APP_WITH_RECORDING } from '../../config/electron';
import { AudioDeviceSelector } from './AudioDevice/audioDeviceSelector';
import { ScrollableContainer } from '../common/ScrollableContainer/ScrollableContainer';

const { languagesHelper, getShortLanguageCode, getValidLocale } = localeUtils;

interface SettingsProps {
    locale: string;
    setLocale: (locale: string) => void;
    updateUsernameRequest: (nickName: string) => void;
    isCallStarted: boolean;
    daemonVersion?: string;
    localization: { getText: (key: string) => string };
    personalUsername?: string;
    telephonyId: string;
    displayTelephonyId: boolean;
    hidePersonalUsername: boolean;
    callIds?: { timestamp: string; callId: string }[];
    copyToClipboard: (payload: string) => void;
    pending: boolean;
    errorCode?: string | null;
    initialized: boolean;
    micDeviceNotAvailable: boolean;
}

class SettingsView extends Component<SettingsProps> {
    static defaultProps: Partial<SettingsProps> = {
        callIds: [],
        daemonVersion: '',
        errorCode: null,
        personalUsername: ''
    };

    state = {
        isDebugInfoOpen: false
    };

    _handleLanguageChange = (locale: string) => {
        const { setLocale, isCallStarted } = this.props;

        if (isCallStarted || !locale) return;

        setLocale(getValidLocale(locale));
    };

    _renderLanguageSelect() {
        const { locale, localization, isCallStarted } = this.props;

        const selectorValues = languagesHelper.map(lang => ({
            key: lang,
            value: localization.getText(`settings.languages.${[lang]}`)
        }));

        const shortLocale = getShortLanguageCode(locale) || undefined;

        return (
            <Card $enabled={!isCallStarted}>
                <CardSubtitle>{localization.getText('settings.title')}</CardSubtitle>
                <RadioButtons selectorValues={selectorValues} selected={shortLocale} onChange={this._handleLanguageChange} />
            </Card>
        );
    }

    _renderInputDeviceSelect() {
        if (!IS_ELECTRON_APP_WITH_RECORDING) return null;

        return (
            <Card $enabled>
                <CardSubtitle>{this.props.localization.getText('settings.inputDevice.title')}</CardSubtitle>
                <AudioDeviceSelector localization={this.props.localization} />
            </Card>
        );
    }

    _renderNickName() {
        const { hidePersonalUsername, localization, personalUsername, pending, updateUsernameRequest } = this.props;

        return !hidePersonalUsername ? (
            <Card $enabled={!pending}>
                <CardSubtitle>{localization.getText('settings.nickname.title')}</CardSubtitle>
                <NickName localization={localization} personalUsername={personalUsername || ''} onSave={updateUsernameRequest} />
            </Card>
        ) : null;
    }

    _renderTelephonyId = () => {
        const { displayTelephonyId, localization, telephonyId: value } = this.props;

        return displayTelephonyId ? (
            <Card $enabled>
                <CardSubtitle>{localization.getText('settings.telephonyId')}</CardSubtitle>
                <TelephonyID value={value} />
            </Card>
        ) : null;
    };

    _toggleDebugInfo = () => {
        this.setState((prevState: { isDebugInfoOpen: boolean }) => ({
            isDebugInfoOpen: !prevState.isDebugInfoOpen
        }));
    };

    _copyDebugInfo = () => {
        const { callIds, copyToClipboard } = this.props;

        copyToClipboard(getDebugInfo([...(callIds || [])]));
    };

    _renderVersion() {
        const { daemonVersion } = this.props;
        return (
            <VersionInfo>
                {daemonVersion ? `daemon: v${daemonVersion}` : null}
                <br />
                coach: v{packageJSON.version}
                {process.env.NODE_ENV === 'production' ? '' : `-${process.env.NODE_ENV}`}
            </VersionInfo>
        );
    }

    render() {
        const { initialized, errorCode, localization } = this.props;

        if (!initialized) {
            return (
                <Wrapper>
                    <Loading>Loading...</Loading>
                </Wrapper>
            );
        }

        return (
            <Wrapper>
                <ScrollableContainer>
                    {errorCode && <ErrorMessage>{localization.getText(`settings.error.${errorCode}`)}</ErrorMessage>}
                    {/*{saved && <SavedMessage>{localization.getText('settings.nickname.success_msg')}</SavedMessage>}*/}
                    {/*{this._renderLanguageSelect()}*/}
                    {this._renderNickName()}
                    {this._renderTelephonyId()}
                    {this._renderInputDeviceSelect()}
                    {this._renderVersion()}
                </ScrollableContainer>
            </Wrapper>
        );
    }
}

function Settings(props: Omit<SettingsProps, 'localization'>) {
    const localization = useLocalization();
    return <SettingsView {...props} localization={localization} />;
}

export default Settings;
